import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ATID } from "@/lib/adminTestIds";
import { LogIn, Lock, KeyRound } from "lucide-react";

export default function AdminLoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);

  React.useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Ingresá email y contraseña.");
      return;
    }
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      const to = u?.must_change_password ? "/admin/cambiar-contrasena" : (location.state?.from || "/admin");
      navigate(to, { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "No se pudo iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotSending(true);
    try {
      await api.passwordResetRequest(forgotEmail.trim().toLowerCase());
      toast.success("Si el correo existe, un administrador procesará tu pedido.");
      setForgotOpen(false);
      setForgotEmail("");
    } catch {
      toast.success("Si el correo existe, un administrador procesará tu pedido.");
      setForgotOpen(false);
    } finally {
      setForgotSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-nightblue flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center mb-8">
          <div className="font-serif text-4xl font-black text-white leading-none">
            Sur<span className="text-watergreen">Primicia</span>
          </div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-white/60 mt-2">
            Panel administrativo
          </div>
        </Link>

        <form onSubmit={submit} className="bg-white border border-softblue p-6 sm:p-8">
          <h1 className="font-serif text-2xl font-bold text-blueblack flex items-center gap-2">
            <Lock className="w-5 h-5 text-turquoise" /> Ingresar
          </h1>
          <p className="mt-1 text-sm text-textgray">
            Acceso exclusivo para redacción.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-textgray font-semibold">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                data-testid={ATID.loginEmail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@surprimicia.com.ar"
                className="mt-2 h-11 bg-lightgray border-softblue focus-visible:ring-turquoise"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-textgray font-semibold">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                data-testid={ATID.loginPassword}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 h-11 bg-lightgray border-softblue focus-visible:ring-turquoise"
              />
            </div>
          </div>

          {error && (
            <div data-testid={ATID.loginError} className="mt-4 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">
              {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid={ATID.loginSubmit}
            disabled={loading}
            className="mt-6 w-full bg-nightblue hover:bg-blueblack text-white rounded-none h-11 font-semibold uppercase text-xs tracking-widest"
          >
            {loading ? "Ingresando…" : (<><LogIn className="w-4 h-4 mr-2" /> Iniciar sesión</>)}
          </Button>

          <div className="mt-4 flex justify-between text-xs text-textgray">
            <button
              type="button"
              data-testid={ATID.loginForgot}
              className="inline-flex items-center gap-1 hover:text-turquoise transition-colors"
              onClick={() => setForgotOpen(true)}
            >
              <KeyRound className="w-3 h-3" /> ¿Olvidaste tu contraseña?
            </button>
            <Link to="/" className="hover:text-turquoise transition-colors">
              ← Volver al portal
            </Link>
          </div>
        </form>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent data-testid={ATID.forgotDialog}>
          <DialogHeader>
            <DialogTitle>Restablecer contraseña</DialogTitle>
            <DialogDescription>
              Ingresá tu email y un administrador procesará tu solicitud. Recibirás una contraseña temporal por un canal seguro.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForgot} className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input
                data-testid={ATID.forgotEmail}
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
              />
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setForgotOpen(false)}>Cancelar</Button>
              <Button data-testid={ATID.forgotSubmit} type="submit" disabled={forgotSending}>
                Enviar pedido
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
